'use client';

import { FormControl, FormField, FormItem } from '@shared/components/shadcn';
import { useTranslations } from 'next-intl';
import { type FieldValues, useFormContext } from 'react-hook-form';
import { FieldInfo } from '../../../../form-controls/FieldInfo/FieldInfo';
import { TextInput } from '../../../../form-controls/TextInput/TextInput';
import type { EmailFieldProps } from './types';

/**
 * EmailField component - A reusable email input field with validation
 *
 * This component provides a complete email input field with:
 * - Proper form integration using React Hook Form
 * - Translation support for labels and placeholders
 * - Error handling and display
 * - Accessibility features
 * - Tracking attributes for analytics
 *
 * @template T - The form values type
 * @param props - The component props
 * @returns The rendered EmailField component
 *
 * @example
 * ```tsx
 * <EmailField formId="registration-form" fieldName="createAccount.email" />
 * ```
 */
function EmailField<T extends FieldValues>({ formId, fieldName, renderErrorMessage, autoComplete = 'email' }: EmailFieldProps<T>) {
  const t = useTranslations('form');
  const form = useFormContext<T>();

  if (!form) {
    throw new Error('EmailField must be used within a FormProvider');
  }

  return (
    <div className="flex flex-col">
      <FieldInfo label={t('labels.email')} htmlFor={`${formId}-email`} />
      <div className="flex flex-row gap-2">
        <FormField
          control={form.control}
          name={fieldName}
          render={({ field, fieldState }) => (
            <FormItem className="flex-1">
              <FormControl>
                <TextInput
                  {...field}
                  id={`${formId}-email`}
                  type="email"
                  autoComplete={autoComplete}
                  placeholder={t('placeholders.email')}
                  helpMessage={((): React.ReactNode => {
                    const raw = typeof fieldState.error?.message === 'string' ? fieldState.error?.message : undefined;
                    const errorType = typeof fieldState.error?.type === 'string' ? (fieldState.error?.type as string) : undefined;
                    return renderErrorMessage ? renderErrorMessage(raw, errorType) : raw;
                  })()}
                  helpMessageMode="focusout"
                  helpMessageType={fieldState.error ? 'error' : undefined}
                  inputTrackingProps={{
                    'data-track-name': 'Email',
                    'data-track-privacy': 'Mask',
                  }}
                  helperTrackingProps={{
                    'data-track-helper': 'Email',
                  }}
                  data-testid={`${formId}-email`}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}

export { EmailField };
